import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = (): boolean => {
    if (!email) {
      setError('Email adresa je obavezna');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Unesite validnu email adresu');
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateEmail()) {
      // Simulate sending reset link
      console.log('Password reset requested for:', email);
      setIsSubmitted(true);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (error) {
      setError('');
    }
  };

  return (
    <div className="bg-gray-50 pt-32 pb-12 min-h-screen">
      {/* Hero section with background */}
      <div
        className="absolute inset-0 h-screen w-full"
        style={{
          backgroundImage: 'url(/assets/go21.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Forgot password card */}
      <div className="relative z-10 max-w-md mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 border border-gray-100">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/assets/go2-logo.png"
              alt="Go2 Logo"
              className="h-10"
            />
          </div>

          {!isSubmitted ? (
            <>
              {/* Heading */}
              <h1 className="text-3xl md:text-4xl font-bold text-go2-pink mb-4 text-center">
                Zaboravljena lozinka?
              </h1>

              {/* Description */}
              <p className="text-gray-600 text-center mb-8">
                Unesi svoju email adresu i poslaćemo ti link za resetovanje lozinke.
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email adresa
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    placeholder="tvoj@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                  />
                  {error && (
                    <p className="text-red-600 text-sm mt-1">{error}</p>
                  )}
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="w-full bg-go2-pink text-white px-8 py-4 rounded-full font-bold text-base uppercase hover:bg-brand-600 transition-all shadow-lg"
                >
                  Pošalji link
                </button>
              </form>

              {/* Back to login */}
              <div className="text-center mt-6">
                <Link
                  to="/prijava"
                  className="text-go2-pink hover:text-brand-600 font-medium"
                >
                  Nazad na prijavu
                </Link>
              </div>
            </>
          ) : (
            <>
              {/* Success state */}
              <div className="text-center">
                <div className="flex justify-center mb-6">
                  <CheckCircle className="text-green-500 w-16 h-16" />
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Link je poslat!
                </h2>

                <p className="text-gray-600 mb-8">
                  Proverite vaš email za dalje instrukcije.
                </p>

                {/* Back to login */}
                <Link
                  to="/prijava"
                  className="inline-block bg-go2-pink text-white px-8 py-4 rounded-full font-bold text-base uppercase hover:bg-brand-600 transition-all shadow-lg"
                >
                  Nazad na prijavu
                </Link>
              </div>
            </>
          )}

          {/* Register link at bottom */}
          <p className="text-center mt-8 text-gray-600 text-sm">
            Nemaš nalog?{' '}
            <Link
              to="/registracija"
              className="text-go2-pink hover:text-brand-600 font-medium"
            >
              Registruj se
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
